import { useSyncExternalStore } from 'react';

// Every open CLI session, held outside React so a terminal outlives the page it was opened on.

// Folded, the dock is exactly its own header. `@dock-minimized-height` in CliDock.less is the other half.
export const CLI_DOCK_MINIMIZED = 40;
export const CLI_DOCK_SHRUNK_WIDTH = 150;

export const CLI_DOCK_DEFAULT_HEIGHT = 380;
export const CLI_DOCK_MIN_HEIGHT = 180;
// What a maximized dock leaves of the page above it.
export const CLI_DOCK_MAX_INSET = 64;

export interface CliRequest {
  seq: number;
  cluster: string;
  targetId?: string | null;
  line?: string;
  run?: boolean;
}

type CliRunning = Record<string, string>;

interface CliDockState {
  sessions: string[];
  active: string | null;
  minimized: boolean;
  shrunk: boolean;
  maximized: boolean;
  height: number;
  inset: number;
  picking: boolean;
  authFor: string | null;
  done: string[];
  request: CliRequest | null;
  running: Record<string, CliRunning>;
}

const NOTHING_RUNNING: CliRunning = {};

const EMPTY: CliDockState = {
  sessions: [],
  active: null,
  minimized: false,
  shrunk: false,
  maximized: false,
  height: CLI_DOCK_DEFAULT_HEIGHT,
  inset: 0,
  picking: false,
  authFor: null,
  done: [],
  request: null,
  running: {},
};

let state: CliDockState = EMPTY;
let requestSeq = 0;
const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

function set(next: Partial<CliDockState>) {
  state = { ...state, ...next };
  listeners.forEach(listener => listener());
}

const without = (list: string[], name: string) =>
  list.filter(entry => entry !== name);

export function openCliSession(clusterName: string) {
  const sessions = state.sessions.includes(clusterName)
    ? state.sessions
    : [...state.sessions, clusterName];
  set({
    sessions,
    active: clusterName,
    minimized: false,
    shrunk: false,
    picking: false,
    authFor: null,
    done: without(state.done, clusterName),
  });
}

export function focusCliSession(clusterName: string) {
  if (!state.sessions.includes(clusterName)) return;
  set({
    active: clusterName,
    minimized: false,
    shrunk: false,
    done: without(state.done, clusterName),
  });
}

// The cluster's own CLI button never ends a session: at worst a stray click folds the dock away.
export function toggleCliSession(clusterName: string) {
  const open = state.sessions.includes(clusterName);
  if (!open) return openCliSession(clusterName);
  if (state.minimized || state.active !== clusterName)
    return focusCliSession(clusterName);
  set({ minimized: true });
}

export function closeCliSession(clusterName: string) {
  const sessions = without(state.sessions, clusterName);
  const active =
    state.active === clusterName
      ? (sessions[sessions.length - 1] ?? null)
      : state.active;
  const { [clusterName]: _closed, ...running } = state.running;
  set({
    sessions,
    active,
    done: without(state.done, clusterName),
    picking: sessions.length ? state.picking : false,
    maximized: sessions.length ? state.maximized : false,
    request: state.request?.cluster === clusterName ? null : state.request,
    running,
  });
}

export function closeCliDock() {
  set(EMPTY);
}

export const setCliDockMinimized = (minimized: boolean) =>
  set({ minimized, shrunk: minimized && state.shrunk });

export const toggleCliDockMinimized = () =>
  set({
    minimized: !state.minimized,
    shrunk: false,
    done: state.minimized ? [] : state.done,
  });

export const toggleCliDockShrunk = () => set({ shrunk: !state.shrunk });

export const toggleCliDockMaximized = () =>
  set({ maximized: !state.maximized, minimized: false, shrunk: false });

export const setCliDockHeight = (height: number) => set({ height });
export const setCliDockInset = (inset: number) => set({ inset });

// Dismissing the picker drops a request still waiting on a token it never got.
export const setCliPicking = (
  picking: boolean,
  authFor: string | null = null,
) =>
  set({
    picking,
    authFor: picking ? authFor : null,
    request:
      picking || !state.request || state.sessions.includes(state.request.cluster)
        ? state.request
        : null,
  });

export function queueCliRequest(request: Omit<CliRequest, 'seq'>) {
  requestSeq += 1;
  set({ request: { ...request, seq: requestSeq } });
}

export function takeCliRequest(seq: number) {
  if (state.request?.seq === seq) set({ request: null });
}

// Opens the cluster's tab when its token is already in, otherwise asks for one; the line waits either way.
export function requestCliLine(
  request: Omit<CliRequest, 'seq'>,
  { unlocked }: { unlocked: boolean },
) {
  queueCliRequest(request);
  if (unlocked || state.sessions.includes(request.cluster))
    openCliSession(request.cluster);
  else setCliPicking(true, request.cluster);
}

export function noteCliRunning(
  clusterName: string,
  targetId: string,
  line: string | null,
) {
  const current = state.running[clusterName] ?? NOTHING_RUNNING;
  if ((current[targetId] ?? null) === line) return;
  const { [targetId]: _finished, ...rest } = current;
  set({
    running: {
      ...state.running,
      [clusterName]: line === null ? rest : { ...rest, [targetId]: line },
    },
  });
}

export function noteCliCommandDone(clusterName: string) {
  if (state.done.includes(clusterName)) return;
  set({ done: [...state.done, clusterName] });
}

export const clearCliCommandDone = (clusterName: string) =>
  state.done.includes(clusterName) &&
  set({ done: without(state.done, clusterName) });

const getState = () => state;

export function useCliDock(): CliDockState {
  return useSyncExternalStore(subscribe, getState, getState);
}

// A maximized dock covers the page anyway.
export function useCliDockHeight(): number {
  return useSyncExternalStore(
    subscribe,
    () =>
      state.sessions.length
        ? state.minimized
          ? CLI_DOCK_MINIMIZED
          : state.height
        : 0,
    () => 0,
  );
}

export function useCliRequest(clusterName: string): CliRequest | null {
  return useSyncExternalStore(
    subscribe,
    () => (state.request?.cluster === clusterName ? state.request : null),
    () => null,
  );
}

export function useCliSessions(): string[] {
  return useSyncExternalStore(
    subscribe,
    () => state.sessions,
    () => EMPTY.sessions,
  );
}

export function useCliHasNews(clusterName: string): boolean {
  return useSyncExternalStore(
    subscribe,
    () => state.done.includes(clusterName),
    () => false,
  );
}

export function useCliRunning(clusterName: string): CliRunning {
  return useSyncExternalStore(
    subscribe,
    () => state.running[clusterName] ?? NOTHING_RUNNING,
    () => NOTHING_RUNNING,
  );
}

export function useCliActiveSession(): string | null {
  return useSyncExternalStore(
    subscribe,
    () => state.active,
    () => null,
  );
}

export type CliSessionState = 'closed' | 'open' | 'showing';

export function useCliSessionState(clusterName: string): CliSessionState {
  const read = (): CliSessionState => {
    if (!state.sessions.includes(clusterName)) return 'closed';
    return state.active === clusterName && !state.minimized
      ? 'showing'
      : 'open';
  };
  return useSyncExternalStore(subscribe, read, () => 'closed');
}

export const hasCliSession = (clusterName: string) =>
  state.sessions.includes(clusterName);

export const isCliPicking = () => state.picking;
